/**
 * ReviewStepScreen: the booking flow's review page (nothing paid online: pay at the session, free,
 * or a request), shared by the Mini App's ConfirmPage and the studio website's phone checkout
 * (shared-client-screens D3, rows V1 to V7). Props only: the pinned photo header, the review head
 * (title, subtitle, the hold line), the When, Duration, Location and price rows (bookingRules'
 * whenLines and reviewPriceRow; a by-phone service's call line in place of Location, service-by-phone
 * D5), the note (approvalNote on a request), and the floating confirm.
 * A booking that did not go through takes the screen as BookingFailureScreen (row V7).
 */
import type { ReactNode, Ref } from 'react';
import { BookingHero, type BookingHeroProps } from '@/components/booking/BookingHero';
import { BookingStepFrame } from '@/components/booking/BookingStepFrame';
import type { PriceRow } from '@/pages/booking/shared/bookingRules';
import { CalendarIcon, CheckIcon, ClockIcon, CreditCardIcon, GlobeIcon, InfoIcon, MapPinIcon, SendIcon, WarningIcon } from './confirmIcons';
import { PhoneCallRow, usePhoneCallAsk, type PhoneCallProps } from './PhoneCallRow';
import styles from './ConfirmPage.module.css';

/** The booking confirm's failure (ConfirmBookingError): its words, and whether Try Again can work. */
export interface BookingFailure {
  readonly code: string;
  readonly message: string;
  readonly canRetry: boolean;
}

/** The failure's title: a hold that ran out, or any other failure. */
export function bookingFailureTitle(failure: BookingFailure): string {
  return failure.code === 'PLACEHOLDER_EXPIRED' ? 'Time slot expired' : 'Booking failed';
}

/** The centered failure state (mockup State 6), no photo header: Try Again when it can work, and Go Back. */
export function BookingFailureScreen({ failure, onRetry, onBack }: { failure: BookingFailure; onRetry: () => void; onBack: () => void }) {
  return (
    <div className={styles.page}>
      <div className={styles.errLayout}>
        <div className={styles.errWrap}>
          <div className={styles.warn}>
            <WarningIcon />
          </div>
          <h1 className={styles.errTitle}>{bookingFailureTitle(failure)}</h1>
          <div className={styles.errMsg}>{failure.message}</div>
        </div>
        <div className={styles.errFoot}>
          <div className={styles.actions}>
            {failure.canRetry && (
              <button type="button" className={styles.btnPrimary} onClick={onRetry}>
                Try Again
              </button>
            )}
            <button type="button" className={styles.btnSecondary} onClick={onBack}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** The review page's placeholder while its reads are in flight (row R4). */
export function ReviewStepLoading() {
  return (
    <div className={styles.page}>
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>Loading your booking...</p>
      </div>
    </div>
  );
}

export interface ReviewStepScreenProps {
  hero: BookingHeroProps;
  onBack: () => void;
  backDisabled?: boolean;
  /** The scroller, for the surface's keyboard rail (the call line's number field). */
  scrollRef?: Ref<HTMLDivElement>;
  title: string;
  subtitle?: string | null;
  /** The hold line ("Time slot reserved until 6:15 PM"); null without one. */
  reserved: string | null;
  /** The When rows (whenLines): one, or each date of a series (row V6). */
  when: readonly string[];
  /** The length ("1 hr", "1 hr per session"). */
  duration: string;
  /** The place, in person or online; null when there is none to name. */
  location: { readonly virtual: boolean; readonly text: string } | null;
  /** A by-phone service's call line in place of the place (service-by-phone D5); null for any other. */
  phoneCall?: PhoneCallProps | null;
  /** The price row (reviewPriceRow). */
  price: PriceRow;
  /** The note under the rows (approvalNote on a request); null for none. */
  note: string | null;
  /** The bottom button: Confirm Booking, Submit Request, or the app's Continue to Payment. */
  confirm: {
    readonly label: string;
    readonly kind: 'confirm' | 'submit' | 'payment';
    readonly onPress: () => void;
    /** Shows the spinner in place of the words while the booking is being made. */
    readonly busy: boolean;
    readonly disabled: boolean;
  };
  /** Drawn over the page (the app's sheets). */
  overlay?: ReactNode;
}

export function ReviewStepScreen({
  hero,
  onBack,
  backDisabled,
  scrollRef,
  title,
  subtitle,
  reserved,
  when,
  duration,
  location,
  phoneCall,
  price,
  note,
  confirm,
  overlay,
}: ReviewStepScreenProps) {
  const ask = usePhoneCallAsk(phoneCall);
  return (
    <BookingStepFrame
      styles={styles}
      onBack={onBack}
      backDisabled={backDisabled}
      scrollRef={scrollRef}
      hero={<BookingHero {...hero} />}
      // Floating footer CTA bar + gradient scrim.
      bar={
        <div className={styles.foot}>
          <div className={styles.actions}>
            {/* The call line's number is missing or being changed: the booking waits for it (D5). */}
            <button type="button" className={styles.btnPrimary} onClick={confirm.onPress} disabled={confirm.disabled || ask.asking}>
              {confirm.busy ? (
                <div className={styles.btnSpinner} />
              ) : (
                <>
                  {confirm.label}
                  {confirm.kind === 'confirm' ? <CheckIcon /> : confirm.kind === 'submit' ? <SendIcon /> : null}
                </>
              )}
            </button>
          </div>
        </div>
      }
      overlay={overlay}
    >
      {/* REVIEW head: title, subtitle and the reserved pill (no icon). */}
      <div className={styles.reviewHead}>
        <h1 className={styles.reviewTitle}>{title}</h1>
        {subtitle && <p className={styles.reviewSubtitle}>{subtitle}</p>}
        {reserved && (
          <div className={styles.reservedInfo}>
            <ClockIcon />
            {reserved}
          </div>
        )}
      </div>

      <div className={styles.det}>
        {when.length > 0 && (
          <div className={styles.row}>
            <CalendarIcon />
            <div>
              <div className={styles.rowLabel}>When</div>
              {when.map((line, i) => (
                <div key={i} className={styles.rowValue}>
                  {line}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.row}>
          <ClockIcon />
          <div>
            <div className={styles.rowLabel}>Duration</div>
            <div className={styles.rowValue}>{duration}</div>
          </div>
        </div>

        {phoneCall ? (
          <PhoneCallRow phoneCall={phoneCall} changing={ask.changing} onChangingChange={ask.setChanging} />
        ) : (
          location && (
            <div className={styles.row}>
              {location.virtual ? <GlobeIcon /> : <MapPinIcon />}
              <div>
                <div className={styles.rowLabel}>Location</div>
                <div className={styles.rowValue}>{location.text}</div>
              </div>
            </div>
          )
        )}

        <div className={styles.row}>
          <CreditCardIcon />
          <div>
            <div className={styles.rowLabel}>{price.label}</div>
            <div className={styles.rowValue}>{price.value}</div>
          </div>
          {price.amount && <span className={styles.rowAmount}>{price.amount}</span>}
        </div>
      </div>

      {note && (
        <div className={styles.notice}>
          <InfoIcon />
          <span className={styles.noticeText}>{note}</span>
        </div>
      )}
    </BookingStepFrame>
  );
}
